import { useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Link, Popover, Typography } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import { getInspectionChecks } from '@/actions/inspectionCheck';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import LoadingMask from '@/components/shared/LoadingMask';
import { useApi } from '@/providers/ApiContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WarningIcon from '@mui/icons-material/Warning';
import ThumbUpOffAltOutlinedIcon from '@mui/icons-material/ThumbUpOffAltOutlined';
import ThumbDownOffAltOutlinedIcon from '@mui/icons-material/ThumbDownOffAltOutlined';
import type { Inspection, EntityId, Manifest } from '@/types';

const PAGE_SIZE = 20;

/** Grid row for draft manifest checks or fetched inspection checks. */
export interface InspectionCheckResult {
  entity_id: EntityId;
  check_section?: string;
  check_name?: string;
  check_details?: string;
  compliant?: boolean | null;
  rationale?: string | null;
  suggestion?: string | null;
  section?: string;
  name?: string;
  manifest_check_id?: EntityId;
  rating?: boolean | null;
  justification?: string | null;
  override_type?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

interface InspectionDocumentManifestChecksProps {
  inspection: Inspection & {
    status: string;
    name: string;
    type: INSPECTION_TYPES;
    [key: string]: unknown;
  };
  document?: {
    document_entity_id?: EntityId;
    document_name?: string;
    [key: string]: unknown;
  } | null;
  manifest?:
    | (Manifest & {
        checks?: Array<{
          section?: string;
          name: string;
          entity_id?: EntityId;
          reference?: string;
          [key: string]: unknown;
        }>;
        global?: boolean;
        reference_document_content?: string;
      })
    | null;
  pending_review_enabled?: boolean;
  on_pending_check_name_click?: (row: InspectionCheckResult) => void;
}

function InspectionDocumentManifestChecks({
  inspection,
  document,
  manifest,
  pending_review_enabled = false,
  on_pending_check_name_click,
}: InspectionDocumentManifestChecksProps): React.JSX.Element {
  const api = useApi();
  const [checks, setChecks] = useState<InspectionCheckResult[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [value, setValue] = useState<string>('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >({
    check_details: false,
  });

  const fetchChecks = useCallback(
    async (page: number, startKey: string | null = null) => {
      if (inspection?.status === INSPECTION_STATUSES.DRAFT || !api) return;

      setLoading(true);
      try {
        const response = await getInspectionChecks(
          api,
          inspection.entity_id,
          document?.document_entity_id,
          manifest?.entity_id,
          PAGE_SIZE,
          startKey || undefined,
        );

        const raw_rows = (response.results || []) as InspectionCheckResult[];
        const sortedResults = raw_rows.sort((a, b) => {
          const aSection = a.check_section || String(a['section'] ?? '');
          const bSection = b.check_section || String(b['section'] ?? '');
          if (aSection === bSection) {
            const aName = a.check_name || String(a['name'] ?? '');
            const bName = b.check_name || String(b['name'] ?? '');
            return aName > bName ? 1 : aName < bName ? -1 : 0;
          } else {
            if (aSection === 'global') {
              return -1;
            } else if (bSection === 'global') {
              return 1;
            } else {
              return aSection > bSection ? 1 : aSection < bSection ? -1 : 0;
            }
          }
        });

        setChecks(sortedResults as InspectionCheckResult[]);
        setHasNextPage(response.last_key !== null);
        const lastKey = response.last_key;
        if (lastKey) {
          setCursors((prev) => ({
            ...prev,
            [page + 1]: lastKey,
          }));
        }
      } catch (error) {
        console.log(error);
        setChecks([]);
      } finally {
        setLoading(false);
      }
    },
    [
      api,
      inspection?.entity_id,
      inspection?.status,
      document?.document_entity_id,
      manifest?.entity_id,
    ],
  );

  useEffect(() => {
    if (inspection?.status !== INSPECTION_STATUSES.DRAFT) {
      setCurrentPage(0);
      setCursors({});
      fetchChecks(0);
    }
  }, [inspection?.status, fetchChecks]);

  const handlePaginationModelChange = useCallback(
    (model: { page: number; pageSize: number }) => {
      if (inspection?.status === INSPECTION_STATUSES.DRAFT) {
        setCurrentPage(model.page);
      } else {
        if (model.page > currentPage && hasNextPage) {
          setCurrentPage(model.page);
          const nextCursor = cursors[model.page];
          if (nextCursor) {
            fetchChecks(model.page, nextCursor);
          }
        } else if (model.page < currentPage) {
          setCurrentPage(model.page);
          if (model.page === 0) {
            fetchChecks(0);
          } else {
            const prevCursor = cursors[model.page];
            if (prevCursor) {
              fetchChecks(model.page, prevCursor);
            }
          }
        }
      }
    },
    [inspection?.status, currentPage, hasNextPage, cursors, fetchChecks],
  );

  const renderCompliant = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.value === true ? (
            <CheckCircleIcon color='success' />
          ) : params.value === false ? (
            <HighlightOffIcon color='error' />
          ) : (
            <WarningIcon color='warning' />
          )}
        </Box>
      );
    },
    [],
  );

  const renderRationale = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      return (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {(params.value as string | null | undefined) || ''}
        </span>
      );
    },
    [],
  );

  const renderSuggestion = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      if (params.row.compliant) {
        return '';
      } else {
        return (
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {(params.value as string | null | undefined) || ''}
          </span>
        );
      }
    },
    [],
  );

  const renderJustification = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      return (
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {(params.value as string | null | undefined) || ''}
        </span>
      );
    },
    [],
  );

  const renderFeedback = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      const is_pending = inspection.status === INSPECTION_STATUSES.PENDING;
      const is_clickable =
        is_pending &&
        pending_review_enabled &&
        Boolean(on_pending_check_name_click);

      const value = params.value as boolean | null | undefined;
      const content =
        value === true ? (
          <ThumbUpOffAltOutlinedIcon fontSize='small' />
        ) : value === false ? (
          <ThumbDownOffAltOutlinedIcon fontSize='small' />
        ) : inspection.status === INSPECTION_STATUSES.PENDING ? (
          <Typography variant='body2' component='span' color='text.secondary'>
            Not Rated
          </Typography>
        ) : (
          ''
        );

      if (!is_clickable || !on_pending_check_name_click) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>{content}</Box>
        );
      }

      return (
        <Link
          component='button'
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            on_pending_check_name_click(params.row);
          }}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            typography: 'body2',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {content}
        </Link>
      );
    },
    [inspection.status, pending_review_enabled, on_pending_check_name_click],
  );

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    const field = (event.currentTarget as HTMLElement).dataset['field'];
    if (field && ['rationale', 'suggestion', 'justification'].includes(field)) {
      const id = (event.currentTarget.parentElement as HTMLElement)?.dataset[
        'id'
      ];
      const row = checks.find((r) => r.entity_id === id);
      if (row) {
        setValue((row[field] as string) || '');
        setAnchorEl(event.currentTarget);
      }
    } else {
      setValue('');
    }
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const render_check_name = useCallback(
    (params: GridRenderCellParams<InspectionCheckResult>) => {
      const label = (params.value as string) || '';
      if (pending_review_enabled && on_pending_check_name_click) {
        return (
          <Link
            component='button'
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              on_pending_check_name_click(params.row);
            }}
            sx={{ cursor: 'pointer', textAlign: 'left', typography: 'body2' }}
          >
            {label}
          </Link>
        );
      }
      return (
        <Typography variant='body2' component='span'>
          {label}
        </Typography>
      );
    },
    [pending_review_enabled, on_pending_check_name_click],
  );

  const draft_columns: GridColDef<InspectionCheckResult>[] = useMemo(
    () => [
      {
        field: 'section',
        headerName: 'Section',
        flex: 0.5,
        type: 'string',
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
      },
    ],
    [],
  );

  const completed_columns = useMemo<GridColDef<InspectionCheckResult>[]>(() => {
    const columns: GridColDef<InspectionCheckResult>[] = [
      {
        field: 'check_section',
        headerName: 'Section',
        flex: 0.5,
        type: 'string',
      },
      {
        field: 'check_name',
        headerName: 'Name',
        flex: 1,
        type: 'string',
        renderCell: render_check_name,
      },
      {
        field: 'check_details',
        headerName: 'Requirement',
        flex: 1,
        type: 'string',
      },
      {
        field: 'compliant',
        headerName: 'Compliant',
        flex: 0.25,
        type: 'boolean',
        renderCell: renderCompliant,
      },
      {
        field: 'rationale',
        headerName: 'Rationale',
        description:
          'The rationale explains how the model determined the compliant status. If compliance is passed, there will be no suggestions shown in this field.',
        flex: 1,
        type: 'string',
        renderCell: renderRationale,
      },
      {
        field: 'suggestion',
        headerName: 'Suggestion',
        description:
          'This field shows recommendations to help you meet the requirement and achieve compliance.',
        flex: 1,
        type: 'string',
        renderCell: renderSuggestion,
      },
    ];

    const show_feedback =
      inspection.status === INSPECTION_STATUSES.PENDING ||
      inspection.status === INSPECTION_STATUSES.COMPLETE;

    if (show_feedback) {
      columns.push({
        field: 'rating',
        headerName: 'Feedback',
        flex: 0.35,
        type: 'boolean',
        sortable: false,
        renderCell: renderFeedback,
      });

      columns.push({
        field: 'justification',
        headerName: 'Justification',
        flex: 1,
        type: 'string',
        renderCell: renderJustification,
      });
    }

    return columns;
  }, [
    inspection.status,
    renderCompliant,
    renderRationale,
    renderSuggestion,
    render_check_name,
    renderFeedback,
    renderJustification,
  ]);

  if (
    inspection.status !== INSPECTION_STATUSES.DRAFT &&
    loading &&
    !checks.length
  ) {
    return <LoadingMask />;
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - 200px)',
        width: '100%',
      }}
    >
      <DataGrid
        pageSizeOptions={[PAGE_SIZE]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: PAGE_SIZE, page: 0 },
          },
        }}
        columnHeaderHeight={35}
        sx={(theme) => ({
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: theme.palette.grey[100],
          },
        })}
        getRowId={(row) => row.entity_id}
        columns={
          inspection?.status === INSPECTION_STATUSES.DRAFT
            ? draft_columns
            : completed_columns
        }
        rows={
          inspection?.status === INSPECTION_STATUSES.DRAFT
            ? ((manifest as Manifest & { checks?: InspectionCheckResult[] })
                ?.checks as InspectionCheckResult[] | undefined) || []
            : checks
        }
        showToolbar
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          cell: {
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
          },
          toolbar: {
            csvOptions: {
              fileName: `${inspection.name} - ${manifest?.name || ''}${inspection.type === INSPECTION_TYPES.EFFECTIVE ? ` - ${document?.document_name || ''}` : ''}`,
            },
            sx: {
              button: {
                color: 'black',
              },
              svg: {
                color: 'black',
              },
            },
            showQuickFilter: true,
          },
        }}
        paginationMode={
          inspection?.status === INSPECTION_STATUSES.DRAFT ? 'client' : 'server'
        }
        rowCount={
          inspection?.status !== INSPECTION_STATUSES.DRAFT
            ? hasNextPage
              ? -1
              : currentPage * PAGE_SIZE + checks.length
            : undefined
        }
        paginationModel={{
          pageSize: PAGE_SIZE,
          page: currentPage,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        loading={loading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
      />
      <Popover
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Typography sx={{ p: 1 }}>{value}</Typography>
      </Popover>
    </Box>
  );
}

export default InspectionDocumentManifestChecks;
